import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  FileText,
  MessageSquare,
  LogOut,
  Menu,
  X,
  Loader2,
  RefreshCw,
  Search,
  Trash2,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  Phone,
  Mail,
  Building,
  MapPin,
  BarChart3,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { getAdminProfile, getStats, getLeads, updateLead, deleteLead, getChatSessions } from '@/lib/api';
import { toast } from 'sonner';

const LOGO_URL = "/logo.svg";
import Logo from '@/components/Logo';
import ChatSessionsWorkspace from '@/components/admin/ChatSessionsWorkspace';
import GscDetailsPanel from '@/components/admin/GscDetailsPanel';


function ChatSessionsTab() {
  var stState = useState(null);
  var chatStats = stState[0]; var setChatStats = stState[1];
  var sessState = useState([]);
  var sessions = sessState[0]; var setSessions = sessState[1];
  var viewState = useState('all');
  var view = viewState[0]; var setView = viewState[1];
  var expandState = useState(null);
  var expandedId = expandState[0]; var setExpandedId = expandState[1];
  var ldState = useState(true);
  var loading = ldState[0]; var setLoading = ldState[1];

  useEffect(function() {
    var token = localStorage.getItem('admin_token');
    var headers = { 'Authorization': 'Bearer ' + token };
    var API = process.env.REACT_APP_BACKEND_URL;
    Promise.all([
      fetch(API + '/api/admin/chat-stats', { headers: headers }).then(function(r) { return r.json(); }).catch(function() { return null; }),
      fetch(API + '/api/admin/chat-sessions', { headers: headers }).then(function(r) { return r.json(); }).catch(function() { return []; }),
    ]).then(function(results) {
      setChatStats(results[0]);
      setSessions(results[1]);
      setLoading(false);
    });
  }, []);

  var filteredSessions = sessions.filter(function(s) {
    if (view === 'leads') return s.lead_info && (s.lead_info.name || s.lead_info.mobile);
    if (view === 'phones') return s.lead_info && s.lead_info.mobile;
    return true;
  });

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;

  return (
    <div className="space-y-6" data-testid="chats-tab">
      {/* Chat Stats */}
      {chatStats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="glass rounded-xl p-5">
            <div className="text-slate-400 text-sm">Total Chats</div>
            <div className="text-3xl font-bold text-white">{chatStats.total_sessions}</div>
          </div>
          <div className="glass rounded-xl p-5">
            <div className="text-slate-400 text-sm">Total Messages</div>
            <div className="text-3xl font-bold text-blue-400">{chatStats.total_messages}</div>
          </div>
          <div className="glass rounded-xl p-5">
            <div className="text-slate-400 text-sm">Leads Captured</div>
            <div className="text-3xl font-bold text-green-400">{chatStats.sessions_with_leads}</div>
          </div>
          <div className="glass rounded-xl p-5">
            <div className="text-slate-400 text-sm">With Phone</div>
            <div className="text-3xl font-bold text-yellow-400">{chatStats.sessions_with_phone}</div>
          </div>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex gap-2">
        <button onClick={function() { setView('all'); }} className={'px-4 py-2 rounded-lg text-sm font-medium ' + (view === 'all' ? 'bg-[#2C39B8] text-white' : 'bg-slate-800 text-slate-400')}>
          All Chats ({sessions.length})
        </button>
        <button onClick={function() { setView('leads'); }} className={'px-4 py-2 rounded-lg text-sm font-medium ' + (view === 'leads' ? 'bg-green-600 text-white' : 'bg-slate-800 text-slate-400')}>
          Leads ({sessions.filter(function(s) { return s.lead_info && (s.lead_info.name || s.lead_info.mobile); }).length})
        </button>
        <button onClick={function() { setView('phones'); }} className={'px-4 py-2 rounded-lg text-sm font-medium ' + (view === 'phones' ? 'bg-yellow-600 text-white' : 'bg-slate-800 text-slate-400')}>
          With Phone ({sessions.filter(function(s) { return s.lead_info && s.lead_info.mobile; }).length})
        </button>
      </div>

      {/* Sessions List */}
      {filteredSessions.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center text-slate-400">No chat sessions yet</div>
      ) : (
        <div className="space-y-3">
          {filteredSessions.map(function(session) {
            var li = session.lead_info || {};
            var isExpanded = expandedId === session.id;
            var msgCount = (session.messages || []).length;
            var lastMsg = session.messages && session.messages.length > 0 ? session.messages[session.messages.length - 1] : null;
            return (
              <div key={session.id} className="glass rounded-xl overflow-hidden">
                {/* Header row — clickable */}
                <div
                  className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                  onClick={function() { setExpandedId(isExpanded ? null : session.id); }}
                  data-testid={'chat-session-' + session.id}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={'w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold ' + (li.mobile ? 'bg-green-600 text-white' : li.name ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400')}>
                        {li.name ? li.name.charAt(0).toUpperCase() : '#'}
                      </div>
                      <div>
                        <div className="text-white font-medium text-sm">
                          {li.name || 'Anonymous Visitor'}
                          {li.company && <span className="text-slate-400 ml-2">@ {li.company}</span>}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                          {li.mobile && <span className="text-green-400 flex items-center gap-1"><Phone size={10} /> {li.mobile}</span>}
                          <span>{msgCount} messages</span>
                          <span>{session.updated_at ? new Date(session.updated_at).toLocaleDateString() + ' ' + new Date(session.updated_at).toLocaleTimeString() : ''}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {li.mobile && (
                        <a
                          href={'https://wa.me/' + li.mobile.replace(/[^0-9]/g, '')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-green-600 hover:bg-green-500 text-white text-xs px-3 py-1.5 rounded-full font-medium"
                          onClick={function(e) { e.stopPropagation(); }}
                        >
                          WhatsApp
                        </a>
                      )}
                      <span className="text-slate-500 text-xs">{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>
                  {/* Last message preview */}
                  {!isExpanded && lastMsg && (
                    <div className="mt-2 text-xs text-slate-500 truncate pl-13">
                      <span className="font-medium">{lastMsg.role === 'user' ? 'Visitor' : 'AI'}:</span> {lastMsg.content.substring(0, 100)}...
                    </div>
                  )}
                </div>

                {/* Expanded: full conversation */}
                {isExpanded && (
                  <div className="border-t border-white/5 p-4 bg-black/20">
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {(session.messages || []).map(function(msg, idx) {
                        return (
                          <div
                            key={idx}
                            className={'p-3 rounded-lg text-sm ' + (msg.role === 'user' ? 'bg-[#2C39B8]/20 text-green-200 ml-8' : 'bg-slate-800 text-slate-300 mr-8')}
                          >
                            <div className="flex justify-between items-center mb-1">
                              <span className="font-semibold text-xs uppercase tracking-wide">{msg.role === 'user' ? 'Visitor' : 'AI Agent'}</span>
                              {msg.timestamp && <span className="text-xs text-slate-500">{new Date(msg.timestamp).toLocaleTimeString()}</span>}
                            </div>
                            <div className="whitespace-pre-wrap">{msg.content}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [seoPages, setSeoPages] = useState([]);
  const [seoPagesTotal, setSeoPagesTotal] = useState(0);
  const [dashStats, setDashStats] = useState(null);
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [linkedinPosts, setLinkedinPosts] = useState([]);
  const [seoGenerating, setSeoGenerating] = useState(false);
  const [visualGenerating, setVisualGenerating] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const profileRes = await getAdminProfile();
        setAdmin(profileRes.data);
        await fetchData();
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const API = process.env.REACT_APP_BACKEND_URL;
      
      const [statsRes, leadsRes, chatsRes, dashRes, seoRes, liStatusRes] = await Promise.all([
        getStats(),
        getLeads(),
        getChatSessions(),
        fetch(`${API}/api/admin/dashboard-stats`, { headers }).then(r => r.json()).catch(() => null),
        fetch(`${API}/api/admin/pages?limit=50`, { headers }).then(r => r.json()).catch(() => null),
        fetch(`${API}/api/linkedin/status`).then(r => r.json()).catch(() => null),
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setChatSessions(chatsRes.data);
      setDashStats(dashRes);
      if (seoRes) { setSeoPages(seoRes.pages || []); setSeoPagesTotal(seoRes.total || 0); }
      setLinkedinStatus(liStatusRes);
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Failed to fetch data');
    }
  };

  const triggerSeoGeneration = async (batchSize = 25) => {
    setSeoGenerating(true);
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/generate?batch_size=${batchSize}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`SEO generation started (${batchSize} pages)`);
    } catch (e) { toast.error('Failed to start generation'); }
    setSeoGenerating(false);
  };

  const triggerVisualGeneration = async (batchSize = 5) => {
    setVisualGenerating(true);
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/generate-visuals?batch_size=${batchSize}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success(`Visual generation started (${batchSize} pages)`);
    } catch (e) { toast.error('Failed to start visual generation'); }
    setVisualGenerating(false);
  };

  const pingSearchEngines = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/ping-search-engines`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      toast.success(`Pinged Google (${data.results?.google?.status}) & Bing (${data.results?.bing?.status})`);
    } catch (e) { toast.error('Failed to ping search engines'); }
  };

  const startScheduler = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/start-scheduler`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Scheduler started: 25 pages/day + LinkedIn posts/hour');
    } catch (e) { toast.error('Failed to start scheduler'); }
  };

  const getLinkedinOAuthUrl = async () => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/linkedin/oauth-url`);
    const data = await res.json();
    if (data.auth_url) {
      window.open(data.auth_url, '_blank');
    } else {
      toast.error(data.error || 'LinkedIn not configured');
    }
  };

  const getLinkedinOrgOAuthUrl = async () => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/linkedin/oauth-url-org`);
    const data = await res.json();
    if (data.auth_url) {
      window.open(data.auth_url, '_blank');
    } else {
      toast.error(data.detail || data.error || 'Company page OAuth not configured');
    }
  };

  const [ltAccess, setLtAccess] = useState('');
  const [ltRefresh, setLtRefresh] = useState('');
  const [ltType, setLtType] = useState('primary');
  const [ltSaving, setLtSaving] = useState(false);
  const saveLinkedinToken = async () => {
    if (!ltAccess.trim()) { toast.error('Paste the access token'); return; }
    setLtSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/linkedin/set-token`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_token: ltAccess.trim(), refresh_token: ltRefresh.trim(), type: ltType })
      });
      const data = await res.json();
      if (res.ok && data.ok) {
        toast.success(ltType === 'primary' ? `Connected: ${data.name}` : `Company page connected (${data.org_urn})`);
        setLtAccess(''); setLtRefresh('');
        window.location.reload();
      } else {
        toast.error(data.detail || 'Token rejected — check scopes (openid + profile + w_member_social)');
      }
    } catch (e) { toast.error('Failed to save token'); }
    setLtSaving(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const handleStatusUpdate = async (leadId, newStatus, notes) => {
    try {
      await updateLead(leadId, { status: newStatus, notes });
      toast.success('Lead updated successfully');
      await fetchData();
      setLeadModalOpen(false);
    } catch (error) {
      toast.error('Failed to update lead');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await deleteLead(leadId);
      toast.success('Lead deleted');
      await fetchData();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.company.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'bg-blue-500/20 text-blue-400', icon: Clock },
      contacted: { color: 'bg-yellow-500/20 text-yellow-400', icon: Phone },
      qualified: { color: 'bg-[#2C39B8]/20 text-blue-400', icon: CheckCircle },
      converted: { color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
      lost: { color: 'bg-red-500/20 text-red-400', icon: AlertCircle },
    };
    const config = statusConfig[status] || statusConfig.new;
    return (
      <Badge className={`${config.color} font-medium`}>
        <config.icon className="w-3 h-3 mr-1" />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#7CC326] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-dashboard">
      {/* Mobile Backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-white/10 transform transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <Logo className="h-8" />
              <span className="text-lg font-bold text-white">Admin</span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            <button
              onClick={() => { setActiveTab('dashboard'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'dashboard'
                  ? 'bg-[#2C39B8]/20 text-[#7CC326]'
                  : 'text-slate-400 hover:bg-slate-800'
              }`}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard size={20} />
              Dashboard
            </button>
            <button
              onClick={() => { setActiveTab('gsc'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeTab === 'gsc' ? 'bg-[#2C39B8]/20 text-[#7CC326]' : 'text-slate-400 hover:bg-slate-800'}`}
              data-testid="nav-gsc"
            >
              <BarChart3 size={20} />
              GSC Details
            </button>
            <button
              onClick={() => { setActiveTab('leads'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'leads'
                  ? 'bg-[#2C39B8]/20 text-[#7CC326]'
                  : 'text-slate-400 hover:bg-slate-800'
              }`}
              data-testid="nav-leads"
            >
              <Users size={20} />
              Leads
              {stats?.new_leads > 0 && (
                <span className="ml-auto bg-[#2C39B8] text-white text-xs px-2 py-1 rounded-full">
                  {stats.new_leads}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('chats'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'chats'
                  ? 'bg-[#2C39B8]/20 text-[#7CC326]'
                  : 'text-slate-400 hover:bg-slate-800'
              }`}
              data-testid="nav-chats"
            >
              <MessageSquare size={20} />
              Chat Sessions
            </button>
            <button
              onClick={() => { setActiveTab('seo'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'seo'
                  ? 'bg-[#2C39B8]/20 text-[#7CC326]'
                  : 'text-slate-400 hover:bg-slate-800'
              }`}
              data-testid="nav-seo"
            >
              <FileText size={20} />
              SEO Pages
              <span className="ml-auto bg-slate-700 text-slate-300 text-xs px-2 py-1 rounded-full">
                {seoPagesTotal}
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('linkedin'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === 'linkedin'
                  ? 'bg-[#2C39B8]/20 text-[#7CC326]'
                  : 'text-slate-400 hover:bg-slate-800'
              }`}
              data-testid="nav-linkedin"
            >
              <Eye size={20} />
              LinkedIn Factory
              {linkedinStatus?.connected && (
                <span className="ml-auto w-2 h-2 bg-green-400 rounded-full" />
              )}
            </button>
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-white/10">
            <div className="text-slate-400 text-sm mb-3">
              Signed in as <span className="text-white">{admin?.email}</span>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="w-full border-slate-700 text-slate-400 hover:text-white"
            >
              <LogOut size={18} className="mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="bg-slate-900/50 border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="lg:hidden text-white p-2"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <h1 className="text-xl font-semibold text-white capitalize">{activeTab}</h1>
          <Button
            variant="ghost"
            onClick={fetchData}
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </Button>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 overflow-auto">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && stats && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="stats-card rounded-xl p-6">
                  <div className="text-slate-400 text-sm mb-2">Total Leads</div>
                  <div className="text-3xl font-bold text-white">{stats.total_leads}</div>
                </div>
                <div className="stats-card rounded-xl p-6">
                  <div className="text-slate-400 text-sm mb-2">New Leads</div>
                  <div className="text-3xl font-bold text-blue-400">{stats.new_leads}</div>
                </div>
                <div className="stats-card rounded-xl p-6">
                  <div className="text-slate-400 text-sm mb-2">Converted</div>
                  <div className="text-3xl font-bold text-green-400">{stats.converted_leads}</div>
                </div>
                <div className="stats-card rounded-xl p-6">
                  <div className="text-slate-400 text-sm mb-2">Chat Sessions</div>
                  <div className="text-3xl font-bold text-blue-400">{stats.chat_sessions}</div>
                </div>
              </div>

              {/* Recent Leads */}
              <div className="glass rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Recent Leads</h3>
                <div className="space-y-3">
                  {leads.slice(0, 5).map((lead) => (
                    <div
                      key={lead.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50"
                    >
                      <div>
                        <div className="text-white font-medium">{lead.name}</div>
                        <div className="text-slate-400 text-sm">{lead.company}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(lead.status)}
                        <span className="text-slate-500 text-sm">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Leads Tab */}
          {activeTab === 'leads' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search leads..."
                    className="pl-10 bg-slate-800 border-slate-700 text-white"
                    data-testid="leads-search"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40 bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Leads Table */}
              <div className="glass rounded-xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-slate-400">Name</TableHead>
                      <TableHead className="text-slate-400">Email</TableHead>
                      <TableHead className="text-slate-400">Company</TableHead>
                      <TableHead className="text-slate-400">Source</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Date</TableHead>
                      <TableHead className="text-slate-400">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredLeads.map((lead) => (
                      <TableRow key={lead.id} className="border-white/10">
                        <TableCell className="text-white font-medium">{lead.name}</TableCell>
                        <TableCell className="text-slate-300">{lead.email}</TableCell>
                        <TableCell className="text-slate-300">{lead.company}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-slate-400 border-slate-600">
                            {lead.source}
                          </Badge>
                        </TableCell>
                        <TableCell>{getStatusBadge(lead.status)}</TableCell>
                        <TableCell className="text-slate-400">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedLead(lead);
                                setLeadModalOpen(true);
                              }}
                              className="text-slate-400 hover:text-white"
                            >
                              <Eye size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteLead(lead.id)}
                              className="text-red-400 hover:text-red-300"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredLeads.length === 0 && (
                  <div className="text-center py-12 text-slate-400">No leads found</div>
                )}
              </div>
            </div>
          )}

          {/* Chats Tab */}
          {activeTab === 'chats' && (
            <ChatSessionsWorkspace />
          )}

          {activeTab === 'gsc' && <GscDetailsPanel />}

          {/* SEO Pages Tab */}
          {activeTab === 'seo' && (
            <div className="space-y-6">
              {/* SEO Stats + Actions */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="glass rounded-xl p-5">
                  <div className="text-slate-400 text-sm">Total Pages</div>
                  <div className="text-2xl font-bold text-white">{dashStats?.content?.seo_pages || seoPagesTotal}</div>
                  <div className="text-slate-500 text-xs mt-1">of {dashStats?.content?.total_possible || 414} possible</div>
                </div>
                <div className="glass rounded-xl p-5">
                  <div className="text-slate-400 text-sm">Completion</div>
                  <div className="text-2xl font-bold text-green-400">{dashStats?.content?.completion_pct || 0}%</div>
                  <div className="w-full bg-slate-700 rounded-full h-2 mt-2">
                    <div className="bg-green-400 rounded-full h-2" style={{ width: `${dashStats?.content?.completion_pct || 0}%` }} />
                  </div>
                </div>
                <div className="glass rounded-xl p-5">
                  <div className="text-slate-400 text-sm">Blog Posts</div>
                  <div className="text-2xl font-bold text-white">{dashStats?.content?.blog_posts || 0}</div>
                </div>
                <div className="glass rounded-xl p-5">
                  <div className="text-slate-400 text-sm">Press Releases</div>
                  <div className="text-2xl font-bold text-white">{dashStats?.content?.press_releases || 0}</div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => triggerSeoGeneration(25)} disabled={seoGenerating} className="bg-[#2C39B8] hover:bg-[#2C39B8]">
                  {seoGenerating ? 'Generating...' : 'Generate 25 Pages'}
                </Button>
                <Button onClick={() => triggerVisualGeneration(5)} disabled={visualGenerating} className="bg-cyan-600 hover:bg-cyan-500">
                  {visualGenerating ? 'Generating...' : 'Generate Visuals (5)'}
                </Button>
                <Button onClick={pingSearchEngines} className="bg-green-600 hover:bg-green-500">
                  Ping Google + Bing
                </Button>
                <Button onClick={async () => {
                  const token = localStorage.getItem('admin_token');
                  try {
                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/indexing-api/submit?batch_size=200`, {
                      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const data = await res.json();
                    if (data.error) {
                      toast.error(data.error);
                    } else {
                      toast.success(`Submitting ${data.total_urls} URLs to Google Indexing API`);
                    }
                  } catch(e) { toast.error('Failed to submit'); }
                }} className="bg-red-600 hover:bg-red-500" data-testid="google-indexing-btn">
                  Google Indexing API
                </Button>
                <Button onClick={async () => {
                  const token = localStorage.getItem('admin_token');
                  try {
                    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seo/indexing-api/submit-bulk-indexnow?batch_size=1000`, {
                      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const data = await res.json();
                    toast.success(`Submitted ${data.total_urls_submitted} URLs via IndexNow`);
                  } catch(e) { toast.error('Failed to submit IndexNow'); }
                }} className="bg-orange-600 hover:bg-orange-500" data-testid="indexnow-bulk-btn">
                  Bulk IndexNow (Bing)
                </Button>
                <Button onClick={startScheduler} className="bg-green-600 hover:bg-green-500">
                  Start Daily Scheduler
                </Button>
                <a href={`${process.env.REACT_APP_BACKEND_URL}/api/seo/sitemap-urls`} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-slate-700 text-slate-300 hover:text-white">
                    Download URL List
                  </Button>
                </a>
              </div>

              {/* SEO Pages Table */}
              <div className="glass rounded-xl overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-slate-400">Title</TableHead>
                      <TableHead className="text-slate-400">Stakeholder</TableHead>
                      <TableHead className="text-slate-400">Industry</TableHead>
                      <TableHead className="text-slate-400">Cluster</TableHead>
                      <TableHead className="text-slate-400">Visual</TableHead>
                      <TableHead className="text-slate-400">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {seoPages.map((pg) => (
                      <TableRow key={pg.slug} className="border-white/5 hover:bg-white/5 cursor-pointer"
                        onClick={() => window.open(`/solutions/${pg.slug}`, '_blank')}
                      >
                        <TableCell className="text-white font-medium max-w-xs truncate">{pg.title}</TableCell>
                        <TableCell className="text-slate-400">{pg.stakeholder}</TableCell>
                        <TableCell className="text-slate-400">{pg.industry}</TableCell>
                        <TableCell><span className="bg-slate-800 text-slate-300 text-xs px-2 py-1 rounded">{pg.cluster || 'manual'}</span></TableCell>
                        <TableCell>{pg.has_visual ? <span className="text-green-400 text-xs">Yes</span> : <span className="text-slate-600 text-xs">No</span>}</TableCell>
                        <TableCell className="text-slate-500 text-sm">{pg.created_at ? new Date(pg.created_at).toLocaleDateString() : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* LinkedIn Factory Tab */}
          {activeTab === 'linkedin' && (
            <div className="space-y-6">
              {/* LinkedIn Connection Status */}
              <div className="glass rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-bold text-lg mb-1">LinkedIn Connection</h3>
                    {linkedinStatus?.connected ? (
                      <div className="text-green-400 text-sm">Connected as {linkedinStatus.name} ({linkedinStatus.email})</div>
                    ) : (
                      <div className="text-[#7CC326] text-sm">Not connected — authorize to enable auto-posting</div>
                    )}
                  </div>
                  <Button onClick={getLinkedinOAuthUrl} className={linkedinStatus?.connected ? 'bg-green-600' : 'bg-blue-600 hover:bg-green-500'} data-testid="connect-linkedin-btn">
                    {linkedinStatus?.connected ? 'Reconnect Chandra' : 'Connect Chandra Profile'}
                  </Button>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-4">
                  <div className="text-slate-300 text-sm">
                    Company Page (ChannelLoyalty) — 6 posts/day
                    <div className="text-slate-500 text-xs">Requires Community Management API + Chandra as Page admin</div>
                  </div>
                  <Button onClick={getLinkedinOrgOAuthUrl} className="bg-blue-600 hover:bg-green-500" data-testid="connect-linkedin-company-btn">
                    Connect Company Page
                  </Button>
                </div>
                {/* Manual paste-token (LinkedIn Token Generator) */}
                <div className="mt-4 border-t border-white/10 pt-4">
                  <div className="text-white font-semibold text-sm mb-1">Paste LinkedIn Token</div>
                  <div className="text-slate-500 text-xs mb-3">From LinkedIn Token Generator. Scopes needed: openid + profile + w_member_social (profile) · add w_organization_social (company). Refresh token optional.</div>
                  <div className="flex flex-col gap-2">
                    <select value={ltType} onChange={(e) => setLtType(e.target.value)} className="bg-slate-800 text-white text-sm rounded px-3 py-2 border border-white/10" data-testid="lt-type-select">
                      <option value="primary">Chandra's Profile (4/day)</option>
                      <option value="org">Company Page (6/day)</option>
                    </select>
                    <textarea value={ltAccess} onChange={(e) => setLtAccess(e.target.value)} placeholder="Access token (AQV...)" rows={2} className="bg-slate-800 text-white text-xs rounded px-3 py-2 border border-white/10 font-mono" data-testid="lt-access-input" />
                    <textarea value={ltRefresh} onChange={(e) => setLtRefresh(e.target.value)} placeholder="Refresh token (optional)" rows={2} className="bg-slate-800 text-white text-xs rounded px-3 py-2 border border-white/10 font-mono" data-testid="lt-refresh-input" />
                    <Button onClick={saveLinkedinToken} disabled={ltSaving} className="bg-[#7CC326] hover:bg-green-500 self-start" data-testid="lt-save-btn">
                      {ltSaving ? 'Saving…' : 'Save & Activate'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* LinkedIn Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="glass rounded-xl p-5">
                  <div className="text-slate-400 text-sm">Total Posts</div>
                  <div className="text-2xl font-bold text-white">{dashStats?.social?.linkedin_posts || 0}</div>
                </div>
                <div className="glass rounded-xl p-5">
                  <div className="text-slate-400 text-sm">Schedule</div>
                  <div className="text-lg font-bold text-white">Every Hour</div>
                  <div className="text-slate-500 text-xs">Auto-generates text + image</div>
                </div>
                <div className="glass rounded-xl p-5">
                  <div className="text-slate-400 text-sm">Image Engine</div>
                  <div className="text-lg font-bold text-white">Nano Banana</div>
                  <div className="text-slate-500 text-xs">Enterprise-grade visuals</div>
                </div>
              </div>

              {/* Manual Trigger */}
              <div className="flex gap-3">
                <Button
                  onClick={async () => {
                    const token = localStorage.getItem('admin_token');
                    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/linkedin/generate-and-post`, {
                      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
                    });
                    toast.success('LinkedIn post generation triggered');
                  }}
                  disabled={!linkedinStatus?.connected}
                  className="bg-blue-600 hover:bg-green-500"
                >
                  Generate & Post Now
                </Button>
                <Button onClick={startScheduler} className="bg-green-600 hover:bg-green-500">
                  Start Hourly Auto-Posting
                </Button>
              </div>

              {/* Post History - would need separate fetch */}
              <div className="glass rounded-xl p-6 text-center text-slate-400">
                {linkedinStatus?.connected 
                  ? 'LinkedIn posts will appear here after generation'
                  : 'Connect LinkedIn to start auto-posting'
                }
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Lead Detail Modal */}
      <Dialog open={leadModalOpen} onOpenChange={setLeadModalOpen}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Lead Details</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-slate-400 text-sm">Name</div>
                  <div className="text-white">{selectedLead.name}</div>
                </div>
                <div>
                  <div className="text-slate-400 text-sm">Company</div>
                  <div className="text-white">{selectedLead.company}</div>
                </div>
                <div>
                  <div className="text-slate-400 text-sm flex items-center gap-1">
                    <Mail size={14} /> Email
                  </div>
                  <div className="text-white">{selectedLead.email}</div>
                </div>
                <div>
                  <div className="text-slate-400 text-sm flex items-center gap-1">
                    <Phone size={14} /> Phone
                  </div>
                  <div className="text-white">{selectedLead.phone || '-'}</div>
                </div>
                <div>
                  <div className="text-slate-400 text-sm flex items-center gap-1">
                    <Building size={14} /> Company Size
                  </div>
                  <div className="text-white">{selectedLead.company_size || '-'}</div>
                </div>
                <div>
                  <div className="text-slate-400 text-sm flex items-center gap-1">
                    <MapPin size={14} /> Geography
                  </div>
                  <div className="text-white">{selectedLead.geography || '-'}</div>
                </div>
              </div>

              {selectedLead.message && (
                <div>
                  <div className="text-slate-400 text-sm">Message</div>
                  <div className="text-white bg-slate-800 rounded-lg p-3 mt-1">
                    {selectedLead.message}
                  </div>
                </div>
              )}

              <div>
                <div className="text-slate-400 text-sm mb-2">Update Status</div>
                <Select
                  value={selectedLead.status}
                  onValueChange={(v) => setSelectedLead({ ...selectedLead, status: v })}
                >
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <div className="text-slate-400 text-sm mb-2">Notes</div>
                <Textarea
                  value={selectedLead.notes || ''}
                  onChange={(e) => setSelectedLead({ ...selectedLead, notes: e.target.value })}
                  className="bg-slate-800 border-slate-700 text-white"
                  rows={3}
                  placeholder="Add notes about this lead..."
                />
              </div>

              <Button
                onClick={() => handleStatusUpdate(selectedLead.id, selectedLead.status, selectedLead.notes)}
                className="w-full bg-[#2C39B8] hover:bg-[#2C39B8]"
              >
                Save Changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
